import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button, notification, Card, InputNumber } from 'antd';
import { SaveOutlined, ArrowLeftOutlined, CalendarOutlined } from '@ant-design/icons';

import { useNavigate, useParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  getInstrument,
  createInstrument,
  updateInstrument,
  getManufacturers,
  getInstrumentCategories,
} from './services/instrument.service.ts';
import Tabs from '../../../../Template/layoutComponents/Tabs/index.jsx';
import InstrumentFileUpload from './components/InstrumentFileUpload';
import VersionHistoryTab from './components/VersionHistoryTab';

const { Option } = Select;

// Função para aplicar máscara de data DD/MM/YYYY
const applyDateMask = (value) => {
  if (!value) return '';

  // Remove tudo que não é dígito
  const cleanValue = value.replace(/\D/g, '');

  // Aplica a máscara
  if (cleanValue.length <= 2) {
    return cleanValue;
  } else if (cleanValue.length <= 4) {
    return `${cleanValue.slice(0, 2)}/${cleanValue.slice(2)}`;
  } else {
    return `${cleanValue.slice(0, 2)}/${cleanValue.slice(2, 4)}/${cleanValue.slice(4, 8)}`;
  }
};

// Função para validar data no formato DD/MM/YYYY
const isValidDate = (dateString) => {
  if (!dateString || dateString.length !== 10) return false;

  const [day, month, year] = dateString.split('/');
  const date = new Date(year, month - 1, day);

  return date.getFullYear() == year && date.getMonth() == month - 1 && date.getDate() == day;
};

const InstrumentForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { plantId, instrumentId } = useParams();
  const { instance } = useMsal();
  const [form] = Form.useForm();
  const [notificationApi, contextHolder] = notification.useNotification();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [manufacturers, setManufacturers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [propertyValues, setPropertyValues] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [instrumentData, setInstrumentData] = useState(null);

  useEffect(() => {
    loadFormData();
  }, []);

  useEffect(() => {
    if (instrumentId && instrumentId !== 'new' && categories.length > 0 && manufacturers.length > 0) {
      setIsEdit(true);
      loadInstrument();
    } else if (!instrumentId || instrumentId === 'new') {
      // Reset do estado quando não for edição
      form.resetFields();
      setInstrumentData(null);
    }
  }, [instrumentId, categories, manufacturers]);

  const loadFormData = async () => {
    try {
      setLoading(true);
      const [manufacturersData, categoriesData] = await Promise.all([
        getManufacturers(instance),
        getInstrumentCategories(instance),
      ]);

      setManufacturers(manufacturersData || []);
      setCategories(categoriesData || []);
    } catch (error) {
      console.error('Erro ao carregar dados do formulário:', error);
      notificationApi.error({
        message: 'Erro',
        description: 'Falha ao carregar dados do formulário',
        duration: 5,
      });
    } finally {
      setLoading(false);
    }
  };

  const loadInstrument = async () => {
    try {
      const data = await getInstrument(instance, plantId, instrumentId);
      
      console.log('InstrumentForm - Data loaded:', data);
      console.log('InstrumentForm - Version History:', data?.versionHistory);
      
      // Armazenar os dados completos do instrumento
      setInstrumentData(data);

      // Extrair os IDs dos objetos
      const categoryId = data.category?._id || data.instrumentCategory?._id;
      const manufacturerId = data.manufacturer?._id;

      // Preparar os dados do formulário
      const formData = {
        name: data.name,
        manufacturer: manufacturerId,
        category: categoryId,
        serialNumber: data.serialNumber,
        modelNumber: data.modelNumber,
        certificateNumber: data.certificateNumber,
      };

      // Processar a data se existir
      if (data.certificate_expiration_date) {
        const dateValue = moment(data.certificate_expiration_date);
        if (dateValue.isValid()) {
          formData.certificate_expiration_date = dateValue.format('DD/MM/YYYY');
        }
      }

      // Setar todos os campos de uma vez
      form.setFieldsValue(formData);

      if (categoryId) {
        const category = categories.find((cat) => (cat.value || cat._id) === categoryId);
        if (category) {
          setSelectedCategory(category);
          if (data.properties && Array.isArray(data.properties)) {
            setSelectedProperties(data.properties.map((prop) => prop.name));
            const values = {};
            data.properties.forEach((prop) => {
              values[prop.name] = prop.value;
            });
            setPropertyValues(values);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar instrumento:', error);
      notificationApi.error({
        message: 'Erro',
        description: 'Falha ao carregar dados do instrumento',
        duration: 5,
      });
    }
  };

  const handleCategoryChange = (categoryValue) => {
    const category = categories.find((cat) => (cat.value || cat._id) === categoryValue);
    setSelectedCategory(category);
    setSelectedProperties([]);
    setPropertyValues({});
  };

  const handlePropertiesChange = (selectedPropertyNames) => {
    setSelectedProperties(selectedPropertyNames);

    const newPropertyValues = { ...propertyValues };

    selectedPropertyNames.forEach((propName) => {
      if (!(propName in newPropertyValues)) {
        newPropertyValues[propName] = '';
      }
    });

    Object.keys(newPropertyValues).forEach((propName) => {
      if (!selectedPropertyNames.includes(propName)) {
        delete newPropertyValues[propName];
      }
    });

    setPropertyValues(newPropertyValues);
  };

  const handlePropertyValueChange = (propertyName, value) => {
    setPropertyValues((prev) => ({
      ...prev,
      [propertyName]: value,
    }));
  };

  const renderPropertyInput = (property) => {
    const { type, name, enumValue } = property;
    const value = propertyValues[name] || '';

    if (type === 'string') {
      return (
        <Input
          placeholder={`Digite ${name}`}
          value={value}
          onChange={(e) => handlePropertyValueChange(name, e.target.value)}
        />
      );
    }

    if (type === 'number') {
      return (
        <InputNumber
          placeholder={`Digite ${name}`}
          value={value}
          onChange={(val) => handlePropertyValueChange(name, val)}
          style={{ width: '100%' }}
        />
      );
    }

    if (type === 'enum') {
      return (
        <Select
          placeholder={`Selecione ${name}`}
          value={value}
          onChange={(val) => handlePropertyValueChange(name, val)}
        >
          {enumValue?.map((option) => (
            <Option key={option} value={option}>
              {option}
            </Option>
          ))}
        </Select>
      );
    }

    return (
      <Input
        placeholder={`Digite ${name}`}
        value={value}
        onChange={(e) => handlePropertyValueChange(name, e.target.value)}
      />
    );
  };

  const handleSubmit = async (values) => {
    try {
      setSaving(true);

      const properties = selectedCategory?.properties || [];
      const finalProperties = selectedProperties
        .map((propName) => {
          const propertyDef = properties.find((p) => p.name === propName);
          return {
            name: propName,
            type: propertyDef?.type || 'string',
            value: propertyValues[propName] || '',
            required: propertyDef?.required || false,
          };
        })
        .filter((prop) => prop.value !== '');

      // Converter data do formato DD/MM/YYYY para YYYY-MM-DD
      let certificateDate = null;
      if (values.certificate_expiration_date && isValidDate(values.certificate_expiration_date)) {
        const [day, month, year] = values.certificate_expiration_date.split('/');
        certificateDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }

      const submitData = {
        name: values.name,
        manufacturer: values.manufacturer,
        category: values.category,
        certificate_expiration_date: certificateDate,
        serialNumber: values.serialNumber,
        modelNumber: values.modelNumber,
        certificateNumber: values.certificateNumber,
        plantId,
        properties: finalProperties,
      };

      if (isEdit) {
        await updateInstrument(instance, plantId, instrumentId, submitData);
        notificationApi.success({
          message: 'Sucesso',
          description: 'Instrumento atualizado com sucesso',
          duration: 3,
        });
      } else {
        await createInstrument(instance, submitData);
        notificationApi.success({
          message: 'Sucesso',
          description: 'Instrumento criado com sucesso',
          duration: 3,
        });
      }

      navigate(`/maintenance/plants/${plantId}/instruments`);
    } catch (error) {
      console.error('Erro ao salvar instrumento:', error);
      notificationApi.error({
        message: 'Erro',
        description: error?.response?.data?.message || 'Falha ao salvar instrumento',
        duration: 5,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      background: '#fafafa'
    }}>
      {contextHolder}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '24px',
          background: 'white',
          borderBottom: '1px solid #f0f0f0',
          flexShrink: 0
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(`/maintenance/plants/${plantId}/instruments`)}
            style={{ marginRight: '16px' }}
          >
            Voltar
          </Button>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>
              {isEdit ? 'Editar Instrumento' : 'Novo Instrumento'}
            </h1>
            <p style={{ margin: '4px 0 0 0', color: '#666' }}>
              {isEdit
                ? 'Edite as informações do instrumento'
                : 'Preencha as informações do novo instrumento'}
            </p>
          </div>
        </div>
        <Button
          type='primary'
          htmlType='submit'
          loading={saving}
          icon={<SaveOutlined />}
          size='large'
          form='instrumentForm'
        >
          {isEdit ? 'Editar' : 'Criar'}
        </Button>
      </div>

      <div style={{ 
        flex: 1, 
        overflow: 'auto',
        padding: '24px'
      }}>
        <Card loading={loading}>
        <Tabs
          tabs={[
            {
              label: t('general_tab') || 'Informações Gerais',
              content: (
                <Form
                  form={form}
                  layout='vertical'
                  onFinish={handleSubmit}
                  style={{ width: '100%' }}
                  id='instrumentForm'
                >
                  <div style={{ display: 'flex', gap: '24px' }}>
                    {/* Coluna da esquerda - Informações principais */}
                    <div style={{ flex: 1, minWidth: '300px' }}>
                      <h3 style={{ marginBottom: '16px' }}>Informações Principais</h3>
                      
                      <Form.Item
                        name='name'
                        label='Nome'
                        rules={[{ required: true, message: 'Por favor, insira o nome do instrumento' }]}
                      >
                        <Input placeholder='Digite o nome do instrumento' />
                      </Form.Item>

                      <Form.Item
                        name='manufacturer'
                        label='Fabricante'
                        rules={[{ required: true, message: 'Por favor, selecione o fabricante' }]}
                      >
                        <Select placeholder='Selecione o fabricante'>
                          {manufacturers.map((manufacturer) => (
                            <Option
                              key={manufacturer.value || manufacturer._id}
                              value={manufacturer.value || manufacturer._id}
                            >
                              {manufacturer.label || manufacturer.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        name='category'
                        label='Categoria do Instrumento'
                        rules={[{ required: true, message: 'Por favor, selecione a categoria' }]}
                      >
                        <Select placeholder='Selecione a categoria' onChange={handleCategoryChange}>
                          {categories.map((category) => (
                            <Option
                              key={category.value || category._id}
                              value={category.value || category._id}
                            >
                              {category.label || category.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        name='certificate_expiration_date'
                        label='Data de Vencimento do Certificado'
                        rules={[
                          { required: true, message: 'Por favor, insira a data de vencimento do certificado' },
                          {
                            validator: async (_, value) => {
                              if (value && !isValidDate(value)) {
                                throw new Error('Data inválida. Use o formato DD/MM/YYYY');
                              }
                            },
                          },
                        ]}
                      >
                        <Input
                          placeholder='DD/MM/YYYY'
                          maxLength={10}
                          onChange={(e) => {
                            const maskedValue = applyDateMask(e.target.value);
                            form.setFieldsValue({ certificate_expiration_date: maskedValue });
                          }}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>

                      <Form.Item
                        name='serialNumber'
                        label='Número de Série'
                        rules={[{ required: true, message: 'Por favor, insira o número de série' }]}
                      >
                        <Input placeholder='Digite o número de série' />
                      </Form.Item>

                      <Form.Item
                        name='modelNumber'
                        label='Número do Modelo'
                        rules={[{ required: true, message: 'Por favor, insira o número do modelo' }]}
                      >
                        <Input placeholder='Digite o número do modelo' />
                      </Form.Item>

                      <Form.Item
                        name='certificateNumber'
                        label='Número do Certificado'
                        rules={[{ required: true, message: 'Por favor, insira o número do certificado' }]}
                      >
                        <Input placeholder='Digite o número do certificado' />
                      </Form.Item>
                    </div>

                    {/* Divisória vertical */}
                    {selectedCategory && selectedCategory.properties && (
                      <div style={{ 
                        width: '1px', 
                        backgroundColor: '#f0f0f0', 
                        margin: '0 12px',
                        alignSelf: 'stretch'
                      }} />
                    )}

                    {/* Coluna da direita - Propriedades */}
                    {selectedCategory && selectedCategory.properties && (
                      <div style={{ flex: 1, minWidth: '300px' }}>
                        <h3 style={{ marginBottom: '16px' }}>Propriedades da Categoria</h3>
                        
                        <Form.Item
                          label='Selecionar Propriedades'
                          help='Escolha as propriedades que deseja preencher'
                        >
                          <Select
                            mode='multiple'
                            placeholder='Selecione as propriedades'
                            value={selectedProperties}
                            onChange={handlePropertiesChange}
                            style={{ width: '100%' }}
                          >
                            {selectedCategory.properties.map((property) => (
                              <Option key={property.name} value={property.name}>
                                <span>
                                  {property.name}
                                  {property.required && <span style={{ color: 'red' }}> *</span>}
                                  <span style={{ color: '#666', fontSize: '12px', marginLeft: '4px' }}>
                                    ({property.type})
                                  </span>
                                </span>
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>

                        {selectedProperties.length > 0 && (
                          <div style={{ marginTop: '24px' }}>
                            <h4 style={{ marginBottom: '16px' }}>Valores das Propriedades</h4>
                            {selectedProperties.map((propName) => {
                              const property = selectedCategory?.properties?.find(
                                (p) => p.name === propName,
                              );
                              if (!property) return null;

                              return (
                                <Form.Item
                                  key={propName}
                                  label={
                                    <span>
                                      {property.name}
                                      {property.required && <span style={{ color: 'red' }}> *</span>}
                                      <span
                                        style={{ color: '#666', fontSize: '12px', marginLeft: '8px' }}
                                      >
                                        ({property.type})
                                      </span>
                                    </span>
                                  }
                                  rules={
                                    property.required
                                      ? [{ required: true, message: `${property.name} é obrigatório` }]
                                      : []
                                  }
                                >
                                  {renderPropertyInput(property)}
                                </Form.Item>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                </Form>
              ),
            },
            {
              label: t('documents_tab') || 'Documentos',
              content: instrumentId ? (
                <InstrumentFileUpload
                  plantId={plantId}
                  instrumentId={instrumentId}
                />
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  Salve o instrumento primeiro para poder anexar documentos.
                </div>
              ),
              disabled: !instrumentId,
            },
            {
              label: t('version_history_tab') || 'Histórico de Versões',
              content: instrumentId && instrumentId !== 'new' ? (
                <VersionHistoryTab instrumentData={instrumentData} />
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  Salve o instrumento primeiro para visualizar o histórico de versões.
                </div>
              ),
              disabled: !instrumentId || instrumentId === 'new',
            },
          ]}
          defaultOpen={0}
        />
        </Card>
      </div>
    </div>
  );
};

export default InstrumentForm;
